<script setup>
import { Form, FormItem } from '@dashhudson/dashing-ui/form';
import { InputText } from '@dashhudson/dashing-ui/input-text';
import { helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  unavailableValues: { type: Array, default: () => [] },
  isValid: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'update:isValid', 'valid', 'commandName']);

const formValue = ref({ keyword: props.modelValue ?? '' });
const defaultValue = ref('');

const trimmed = computed(() => {
  const { keyword } = formValue.value ?? {};
  return (keyword ?? '').trim();
});

const rules = computed(() => ({
  keywordExists: helpers.withMessage(
    'Keyword already exists',
    () => !props.unavailableValues.includes(trimmed.value),
  ),
  required: helpers.withMessage('Keyword cannot be empty', required),
  shouldNotEmpty: helpers.withMessage('Keyword cannot be empty', minLength(1)),
  maxLength: helpers.withMessage('Keyword cannot exceed 50 characters', maxLength(50)),
  invalidDoubleQuote: helpers.withMessage(
    "Use double quotes to isolate a single keyword only. Don't include multiple words or any text outside the quotes.",
    () => {
      const quoteCount = (trimmed.value.match(/"/g) || []).length;
      if (quoteCount < 2) {
        return true;
      }
      return /^"[^" ]+"$/.test(trimmed.value);
    },
  ),
}));

const onValidation = (newVal) => {
  const valid = !newVal.$invalid;
  // Filter button dropdown
  emit('update:isValid', valid);
  // Menu form
  emit('valid', valid);
};

watch(
  () => formValue.value?.keyword,
  (to) => {
    emit('update:modelValue', to);
  },
  { immediate: true },
);

onBeforeMount(() => {
  emit('commandName', 'Apply');
  defaultValue.value = props.modelValue ?? '';
});

onBeforeUnmount(() => {
  formValue.value = { keyword: defaultValue.value };
});
</script>

<template>
  <Form ref="form" v-model="formValue" class="dash-px-1" @validation="onValidation">
    <div class="dash-flex dash-flex-col dash-gap-4">
      <FormItem
        v-slot="{ inputProps, inputEvents }"
        name="keyword"
        label="Keyword"
        :rules="rules"
        required
      >
        <InputText v-bind="inputProps" placeholder="Enter Keyword" v-on="inputEvents" />
      </FormItem>
    </div>
  </Form>
</template>
